import React, { useMemo, useState } from 'react';
import { X, BadgePercent } from 'lucide-react';
import { ProposalDiscount } from '../../types';
import './ProposalDiscountModal.css';

interface ProposalDiscountModalProps {
    discount?: ProposalDiscount;
    /** Current post-margin billable total — the discount is clamped to this. */
    billableTotal: number;
    readOnly?: boolean;
    onSave: (discount: ProposalDiscount) => void;
    onClose: () => void;
}

const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value);

const ProposalDiscountModal: React.FC<ProposalDiscountModalProps> = ({
    discount,
    billableTotal,
    readOnly = false,
    onSave,
    onClose,
}) => {
    const [enabled, setEnabled] = useState(discount?.enabled ?? false);
    const [amountInput, setAmountInput] = useState(
        discount?.amount ? String(discount.amount) : ''
    );
    const [label, setLabel] = useState(discount?.label ?? '');
    const [clientDescription, setClientDescription] = useState(discount?.clientDescription ?? '');
    const [internalNotes, setInternalNotes] = useState(discount?.internalNotes ?? '');

    const maxDiscount = Math.max(0, Math.round(billableTotal));
    const parsedAmount = useMemo(() => {
        const n = Number(amountInput.replace(/[$,\s]/g, ''));
        if (!Number.isFinite(n) || n < 0) return 0;
        return Math.round(n);
    }, [amountInput]);
    const clampedAmount = Math.min(parsedAmount, maxDiscount);
    const amountExceedsTotal = parsedAmount > maxDiscount;

    const canSave = !readOnly && (!enabled || (clampedAmount > 0 && label.trim().length > 0));

    const handleSave = () => {
        if (!canSave) return;
        onSave({
            enabled,
            amount: clampedAmount,
            label: label.trim(),
            clientDescription: clientDescription.trim() || undefined,
            internalNotes: internalNotes.trim() || undefined,
        });
    };

    return (
        <div className="discount-modal-overlay" onClick={onClose} role="presentation">
            <div className="discount-modal" onClick={e => e.stopPropagation()}>
                <div className="dm-header">
                    <div className="dm-header-left">
                        <BadgePercent size={20} />
                        <div>
                            <h2>Promotional Discount</h2>
                            <p>Flat dollars off the client total, in exchange for a benefit</p>
                        </div>
                    </div>
                    <button className="dm-close" onClick={onClose} aria-label="Close">
                        <X size={18} />
                    </button>
                </div>

                <div className="dm-body">
                    <label className="dm-enable-row">
                        <input
                            type="checkbox"
                            checked={enabled}
                            disabled={readOnly}
                            onChange={e => setEnabled(e.target.checked)}
                        />
                        <span>Apply a discount to this proposal</span>
                    </label>

                    <div className={`dm-fields ${enabled ? '' : 'dm-fields-disabled'}`}>
                        <div className="dm-field">
                            <label htmlFor="dm-label">Client-facing label</label>
                            <input
                                id="dm-label"
                                type="text"
                                value={label}
                                disabled={readOnly || !enabled}
                                placeholder="e.g. Feature Film Partnership"
                                onChange={e => setLabel(e.target.value)}
                            />
                        </div>

                        <div className="dm-field">
                            <label htmlFor="dm-amount">Discount amount</label>
                            <div className="dm-amount-wrap">
                                <span className="dm-amount-prefix">$</span>
                                <input
                                    id="dm-amount"
                                    type="text"
                                    inputMode="numeric"
                                    value={amountInput}
                                    disabled={readOnly || !enabled}
                                    placeholder="30,000"
                                    onChange={e => setAmountInput(e.target.value)}
                                />
                            </div>
                            {amountExceedsTotal && (
                                <p className="dm-hint dm-warn">
                                    Capped at the current proposal total ({formatCurrency(maxDiscount)}).
                                </p>
                            )}
                        </div>

                        <div className="dm-field">
                            <label htmlFor="dm-client-desc">Client-facing description</label>
                            <textarea
                                id="dm-client-desc"
                                rows={2}
                                value={clientDescription}
                                disabled={readOnly || !enabled}
                                placeholder="Short description of the exchange the client will see, e.g. “In exchange for feature film rights during construction.”"
                                onChange={e => setClientDescription(e.target.value)}
                            />
                        </div>

                        <div className="dm-field">
                            <label htmlFor="dm-internal-notes">Internal notes (staff only)</label>
                            <textarea
                                id="dm-internal-notes"
                                rows={2}
                                value={internalNotes}
                                disabled={readOnly || !enabled}
                                placeholder="Context for the team — never shown to the client."
                                onChange={e => setInternalNotes(e.target.value)}
                            />
                        </div>
                    </div>

                    {enabled && clampedAmount > 0 && (
                        <div className="dm-preview">
                            <span className="dm-preview-label">
                                {label.trim() || 'Discount'}
                            </span>
                            <span className="dm-preview-amount">−{formatCurrency(clampedAmount)}</span>
                        </div>
                    )}
                </div>

                <div className="dm-footer">
                    <button className="dm-btn dm-btn-secondary" onClick={onClose}>
                        Cancel
                    </button>
                    <button className="dm-btn dm-btn-primary" onClick={handleSave} disabled={!canSave}>
                        Save Discount
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProposalDiscountModal;
